import { FC, useEffect, useState } from "react";
import OnboardingForm from "@components/Onboarding/OnboardingForm/OnboardingForm";
import { useTranslation } from "react-i18next";
import { FieldValues, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  OnboardingFormActionContainer,
  OnboardingFormContainer,
} from "@components/Onboarding/OnboardingForm";
import { useNavigate } from "react-router-dom";
import {
  useCreateAccountMutation,
  useGetAccountsQuery,
  useUpdateAccountMutation,
} from "@store/accountApi";
import { Account } from "@models/account";
import { setAccountId } from "@store/accountSlice";
import styled from "@emotion/styled";
import { setMessage } from "@store/snackbarSlice";
import AccountForm from "@components/AccountForm/AccountForm";
import { PermContactCalendar } from "@mui/icons-material";
import { useGetUserQuery, useUpdateUserMutation } from "@store/userApi";
import { User } from "@models/user";

const FormContainer = styled(OnboardingFormContainer)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OnboardingAccountForm: FC = () => {
  const { t } = useTranslation();
  const { data: accounts } = useGetAccountsQuery();
  const { data: user } = useGetUserQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const form = useForm<Partial<Account & User>>({
    mode: "onChange",
    criteriaMode: "all",
  });
  const [createAccount] = useCreateAccountMutation();
  const [updateAccount] = useUpdateAccountMutation();
  const [updateUser] = useUpdateUserMutation();
  const [isSaving, setIsSaving] = useState(false);

  const {
    handleSubmit,
    clearErrors,
    formState: { isSubmitting },
    reset,
  } = form;

  useEffect(() => {
    if (isSaving) {
      return;
    }

    const account = accounts?.data?.[0];

    reset({
      language: account?.language || undefined,
      type: account?.type || undefined,
      country: account?.country || undefined,
      city: account?.city || undefined,
      institutionName: account?.institutionName || undefined,
      institutionType: account?.institutionType || undefined,
      numberPatients: account?.numberPatients || undefined,
      firstName: user?.firstName || undefined,
      lastName: user?.lastName || undefined,
    });
  }, [accounts, user, reset, isSaving]);

  const updateUserDetails = async (user: Partial<User>) => {
    const userRes = await updateUser(user);

    if (userRes.error) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));

      return false;
    }

    return true;
  };

  const updateAccountDetails = async (
    account: Partial<Account>,
    id?: string,
  ) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const accRes = await (id
      ? updateAccount({ id, ...account, tz })
      : createAccount({ ...account, tz }));

    if (accRes.error) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));

      return false;
    }

    dispatch(setAccountId(accRes.data.id));

    return true;
  };

  const save = async (values: FieldValues) => {
    try {
      setIsSaving(true);
      const { firstName, lastName, ...account } = values;

      const userUpdated = await updateUserDetails({ firstName, lastName });
      if (!userUpdated) {
        return;
      }

      const accountUpdated = await updateAccountDetails(
        account,
        accounts?.data?.[0]?.id,
      );

      if (accountUpdated) {
        navigate("/onboarding/profile");
      }
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <OnboardingForm
      title={t("account.title")}
      subtitle={t("account.subtitle")}
      icon={<PermContactCalendar fontSize="small" />}
    >
      <FormContainer
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(save)(e);
        }}
      >
        <AccountForm form={form} />

        <OnboardingFormActionContainer isSubmitting={isSubmitting} />
      </FormContainer>
    </OnboardingForm>
  );
};

export default OnboardingAccountForm;
